import { useEffect, useState, useCallback } from 'react';
import { CheckCircle2, History, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import PlayerNameLink from '../../components/PlayerNameLink';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { useShowMore } from '../../components/useShowMore';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const RANGE_OPTIONS = [
    { days: 7,  label: '7 d' },
    { days: 30, label: '30 d' },
    { days: 90, label: '90 d' },
];

const formatDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('en-US', {
            timeZone: 'America/Chicago',
            month: 'short', day: 'numeric',
            hour: 'numeric', minute: '2-digit',
        });
    } catch { return iso; }
};

const PaidPayoutsPanel = () => {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(false);

    const fetchPaid = useCallback(async (d) => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/paid-payouts?days=${d}`,
                { withCredentials: true });
            setData(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load paid payouts');
        }
        setLoading(false);
    }, []);

    useEffect(() => { fetchPaid(days); }, [days, fetchPaid]);

    const total = data?.total_amount || 0;
    const count = data?.count || 0;
    const byMethod = data?.by_method || [];
    const allRows = data?.rows || [];
    const { visibleItems: visibleRows, ShowMoreButton, total: rowTotal, limit: rowLimit } = useShowMore(allRows, 10, 10);

    return (
        <div className="card-dark p-6 mb-4" data-testid="paid-payouts-panel">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-xl bg-[#39FF14]/15 flex items-center justify-center">
                        <History className="w-5 h-5 text-[#39FF14]" />
                    </div>
                    <div>
                        <h3 className="text-base font-bold text-white">Paid Payouts</h3>
                        <p className="text-[11px] text-[#A0AAB2]">Reconciliation log of completed payments</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {/* Range pills */}
                    <div className="flex gap-1 bg-white/5 rounded-full p-0.5">
                        {RANGE_OPTIONS.map(opt => (
                            <button
                                key={opt.days}
                                onClick={() => setDays(opt.days)}
                                className={`px-2.5 py-1 text-[10px] font-bold rounded-full transition-all ${
                                    days === opt.days
                                        ? 'bg-[#39FF14] text-black'
                                        : 'text-[#A0AAB2] hover:text-white'
                                }`}
                                data-testid={`paid-range-${opt.days}`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                    <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-[#39FF14]/15 text-[#39FF14] border border-[#39FF14]/40">
                        ${total.toFixed(2)} · {count}
                    </span>
                </div>
            </div>

            {/* By-method breakdown */}
            {!loading && byMethod.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                    {byMethod.map(m => (
                        <span
                            key={m.method}
                            className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-white/5 text-white border border-white/10"
                        >
                            <span className="text-[#FFD700]">{m.method}</span>
                            <span className="text-[#A0AAB2] ml-1.5">${m.total.toFixed(2)} · {m.count}</span>
                        </span>
                    ))}
                </div>
            )}

            {loading ? (
                <div className="flex justify-center py-8">
                    <Loader2 className="w-5 h-5 text-[#39FF14] animate-spin" />
                </div>
            ) : count === 0 ? (
                <div className="text-center py-8">
                    <CheckCircle2 className="w-10 h-10 text-[#A0AAB2]/40 mx-auto mb-2" />
                    <p className="text-[#A0AAB2] text-sm">No paid payouts in the last {days} day{days === 1 ? '' : 's'}.</p>
                </div>
            ) : (
                <>
                    {/* Expand toggle */}
                    <button
                        type="button"
                        onClick={() => setExpanded((v) => !v)}
                        className="text-[11px] text-[#A0AAB2] hover:text-white inline-flex items-center gap-1 mb-2"
                        data-testid="paid-payouts-toggle"
                    >
                        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        {expanded ? 'Hide details' : `Show ${count} entr${count === 1 ? 'y' : 'ies'}`}
                    </button>

                    {expanded && (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="border-white/10">
                                        <TableHead className="text-[#A0AAB2]">Player</TableHead>
                                        <TableHead className="text-[#A0AAB2] text-right">Amount</TableHead>
                                        <TableHead className="text-[#A0AAB2]">Method</TableHead>
                                        <TableHead className="text-[#A0AAB2]">Sent To</TableHead>
                                        <TableHead className="text-[#A0AAB2]">Paid</TableHead>
                                        <TableHead className="text-[#A0AAB2]">Note</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {visibleRows.map((r) => {
                                        const amt = Math.abs(r.cash_sent ?? r.amount ?? 0);
                                        return (
                                            <TableRow
                                                key={r.id}
                                                className="border-white/10"
                                                data-testid={`paid-payout-row-${r.id}`}
                                            >
                                                <TableCell>
                                                    <div className="text-white text-sm font-medium">
                                                        <PlayerNameLink id={r.user_id} name={r.user_name} testId={`paid-payout-name-${r.id}`} />
                                                    </div>
                                                    <div className="text-[#A0AAB2] text-[11px]">{r.user_email}</div>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <div className="font-mono font-black text-[#39FF14]">${amt.toFixed(2)}</div>
                                                    {(r.status !== 'approved' || r.payment_count > 1) && (
                                                        <div className="text-[10px] text-[#A0AAB2] font-mono" data-testid={`paid-payout-note-${r.id}`}>
                                                            {r.status === 'played_back' ? 'partial · rest played back' : r.status === 'verify_hold' ? 'on hold' : r.paid ? `${r.payment_count} payments` : 'partial · still open'}
                                                        </div>
                                                    )}
                                                    {r.large_payout && (
                                                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#FF007F]/15 text-[#FF007F] border border-[#FF007F]/40">
                                                            LARGE
                                                        </span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {r.payout_method ? (
                                                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#FFD700]/15 text-[#FFD700] border border-[#FFD700]/30">
                                                            {r.payout_method}
                                                        </span>
                                                    ) : (
                                                        <span className="text-[#666] text-xs italic">—</span>
                                                    )}
                                                </TableCell>
                                                <TableCell className="font-mono text-xs text-white/80">
                                                    {r.payout_handle || <span className="text-[#666] italic">in-person</span>}
                                                </TableCell>
                                                <TableCell className="text-[#A0AAB2] text-xs">
                                                    {formatDate(r.paid_at)}
                                                </TableCell>
                                                <TableCell className="text-xs text-[#A0AAB2] max-w-[200px] truncate" title={r.paid_note || ''}>
                                                    {r.paid_note || '—'}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                            <p className="text-[10px] text-[#A0AAB2] text-center mt-2">
                                Showing {Math.min(rowLimit, rowTotal)} of {rowTotal}
                            </p>
                            <ShowMoreButton testid="paid-payouts-show-more" />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default PaidPayoutsPanel;
